// Student CoursesPage — course catalog with an Enrol action per card.
import { useMemo, useState } from "react";
import { useFetch, postJson } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import type { Course, Enrollment, PaginatedResult } from "@dm-lms/shared/types";

const SKELETON_COUNT = 6;

export default function CoursesPage() {
  const { data: coursesData, isLoading, error } = useFetch<PaginatedResult<Course>>(
    "/api/courses?page=1&pageSize=100"
  );
  const { data: enrollmentsData, refetch: refetchEnrollments } = useFetch<PaginatedResult<Enrollment>>(
    "/api/enrollments/mine?page=1&pageSize=100"
  );

  const enrolledCourseIds = useMemo(
    () => new Set((enrollmentsData?.items ?? []).map((enrollment) => enrollment.courseId)),
    [enrollmentsData]
  );

  const [enrollingId, setEnrollingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function enrol(courseId: string) {
    setEnrollingId(courseId);
    setErrors((prev) => ({ ...prev, [courseId]: "" }));

    const result = await postJson<Enrollment>("/api/enrollments", { courseId });
    setEnrollingId(null);
    if (!result.ok) {
      setErrors((prev) => ({ ...prev, [courseId]: result.error }));
      return;
    }
    refetchEnrollments();
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Courses</h1>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-5 animate-pulse">
              <div className="h-5 w-3/4 bg-gray-200 rounded mb-3" />
              <div className="h-4 w-16 bg-gray-200 rounded-full mb-4" />
              <div className="h-4 w-1/2 bg-gray-200 rounded mb-2" />
              <div className="h-4 w-1/3 bg-gray-200 rounded mb-4" />
              <div className="h-9 w-full bg-gray-200 rounded-md" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && (
        (coursesData?.items ?? []).length === 0 ? (
          <p className="text-dm-slate text-sm">No courses are open for enrollment right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(coursesData?.items ?? []).map((course) => {
              const isEnrolled = enrolledCourseIds.has(course.id);
              const isEnrolling = enrollingId === course.id;
              const courseError = errors[course.id];

              return (
                <div key={course.id} className="rounded-lg border border-gray-200 bg-white p-5 flex flex-col">
                  <h2 className="font-medium text-dm-navy mb-2">{course.title}</h2>
                  <div className="mb-4">
                    <Badge tone="gray">{course.mode}</Badge>
                  </div>
                  <p className="text-sm text-dm-slate mb-1">{course.durationHours} hours</p>
                  <p className="text-sm text-dm-slate mb-4">KES {course.price.toLocaleString()}</p>

                  <div className="mt-auto">
                    {isEnrolled ? (
                      <Badge tone="green">Enrolled</Badge>
                    ) : (
                      <button
                        type="button"
                        className="w-full bg-dm-gold text-dm-navy text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
                        disabled={isEnrolling}
                        onClick={() => enrol(course.id)}
                      >
                        {isEnrolling ? "Enrolling…" : "Enrol"}
                      </button>
                    )}
                    {courseError && <p className="text-red-600 text-xs mt-2">{courseError}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
